import { PositionType } from 'data/types';
import { Shape } from 'three';
import {
  CardinalLocal3DEdge,
  Local3DEdge,
  Local3DPoint
} from 'utils/3d-edge-types';
import { EdgeInterval } from 'utils/polygon-shared-edges';
import { rectToVertices, RelativeVertex } from 'utils/space-polygon';

export type { CardinalLocal3DEdge, Local3DEdge, Local3DPoint };

export type WallSegment3D = {
  centerX: number;
  centerZ: number;
  length: number;
  angle: number;
};

export function getSpacePolygonVerticesFeet(
  vertices: PositionType[] | undefined,
  bboxWidthFeet: number,
  bboxHeightFeet: number
): RelativeVertex[] {
  if (vertices && vertices.length >= 3) {
    return vertices;
  }
  return rectToVertices(bboxWidthFeet, bboxHeightFeet);
}

export function verticesToLocal3D(
  vertices: RelativeVertex[],
  bboxWidthFeet: number,
  bboxHeightFeet: number,
  scale: number
): Local3DPoint[] {
  const halfWidth = (bboxWidthFeet * scale) / 2;
  const halfDepth = (bboxHeightFeet * scale) / 2;
  return vertices.map((v) => ({
    x: v.x * scale - halfWidth,
    z: v.y * scale - halfDepth
  }));
}

export function buildSpaceFloorShape(localVertices: Local3DPoint[]): Shape {
  const shape = new Shape();
  if (localVertices.length === 0) return shape;

  shape.moveTo(localVertices[0].x, -localVertices[0].z);
  for (let i = 1; i < localVertices.length; i += 1) {
    shape.lineTo(localVertices[i].x, -localVertices[i].z);
  }
  shape.closePath();
  return shape;
}

export function getPolygonSignedArea(localVertices: Local3DPoint[]): number {
  let sum = 0;
  for (let i = 0; i < localVertices.length; i += 1) {
    const p1 = localVertices[i];
    const p2 = localVertices[(i + 1) % localVertices.length];
    sum += p1.x * p2.z - p2.x * p1.z;
  }
  return sum / 2;
}

function buildEdgeWallSegment(
  p1: Local3DPoint,
  p2: Local3DPoint,
  inwardSign: 1 | -1,
  halfThickness: number
): WallSegment3D | null {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return null;
  const inwardNormalX = (-dz / length) * inwardSign;
  const inwardNormalZ = (dx / length) * inwardSign;
  const midX = (p1.x + p2.x) / 2;
  const midZ = (p1.z + p2.z) / 2;
  return {
    centerX: midX + inwardNormalX * halfThickness,
    centerZ: midZ + inwardNormalZ * halfThickness,
    length,
    angle: -Math.atan2(dz, dx)
  };
}

export function buildSpaceWallSegments(
  localVertices: Local3DPoint[],
  wallThickness: number,
  visibleIntervalsPerEdge?: EdgeInterval[][]
): WallSegment3D[] {
  if (localVertices.length < 3) return [];

  const inwardSign = getPolygonSignedArea(localVertices) >= 0 ? 1 : -1;
  const halfThickness = wallThickness / 2;

  const segments: WallSegment3D[] = [];
  for (let i = 0; i < localVertices.length; i += 1) {
    const p1 = localVertices[i];
    const p2 = localVertices[(i + 1) % localVertices.length];
    const intervals = visibleIntervalsPerEdge?.[i] ?? [{ start: 0, end: 1 }];
    for (const interval of intervals) {
      const sub1: Local3DPoint = {
        x: p1.x + (p2.x - p1.x) * interval.start,
        z: p1.z + (p2.z - p1.z) * interval.start
      };
      const sub2: Local3DPoint = {
        x: p1.x + (p2.x - p1.x) * interval.end,
        z: p1.z + (p2.z - p1.z) * interval.end
      };
      const segment = buildEdgeWallSegment(
        sub1,
        sub2,
        inwardSign,
        halfThickness
      );
      if (segment) segments.push(segment);
    }
  }
  return segments;
}

export function localPointFromVertexFeet(
  vertex: PositionType,
  bboxWidthFeet: number,
  bboxHeightFeet: number,
  scale: number
): Local3DPoint {
  return {
    x: vertex.x * scale - (bboxWidthFeet * scale) / 2,
    z: vertex.y * scale - (bboxHeightFeet * scale) / 2
  };
}
